import { cx } from '@lastbrand/web-ui-styled-system/css'
import { useTranslation } from 'next-i18next'
import React, { useId } from 'react'

import * as styles from './product-property-ds.styles'

// Figma: Quince Design System 2.0 → Widgets → ProductProperty (node 8220:16881).
// Formalizes the product-option header row ("Property: Value" + optional inline
// action button) above an options-group slot, previously inlined in
// renderProductOption (src/quince-components/src/atoms/productForm/product-form-shared.tsx).
export interface ProductPropertyDSProps {
  /** Figma `textProperty` — option display name, without the trailing colon ("Color"). */
  label: React.ReactNode
  /** Figma `textValue` — selected value display name. sr-only "not selected" announced when absent. */
  selectedValue?: React.ReactNode
  /** Suppresses the ":" separator (Input / Text Box options render no inline value). */
  hideSeparator?: boolean
  /** Figma `hasButton` — inline action slot (e.g. the Size Chart trigger). */
  action?: React.ReactNode
  /** Preserves the existing `legend_<displayName>` e2e locator on the header. */
  headerTestId?: string
  /** 'fieldset' (default): header is a <legend>. 'div': the content wrapper gets role="group" + aria-labelledby of the label span — the action slot stays out of the group's accessible name. */
  as?: 'fieldset' | 'div'
  className?: string
  headerClassName?: string
  contentClassName?: string
  children: React.ReactNode
}

const ProductPropertyDS = React.forwardRef<HTMLFieldSetElement | HTMLDivElement, ProductPropertyDSProps>(
  (
    {
      label,
      selectedValue,
      hideSeparator,
      action,
      headerTestId,
      as = 'fieldset',
      className,
      headerClassName,
      contentClassName,
      children,
    },
    ref,
  ) => {
    const { t } = useTranslation('common')
    const headerId = useId()

    const header = (
      <>
        {/* The group id lives on the label span, not the header row, so action text
            (e.g. "Size Chart") never leaks into the group's accessible name. */}
        <span id={as === 'div' ? headerId : undefined} className={styles.labelCss}>
          {label}
          {!hideSeparator && ': '}
          {selectedValue ? (
            <span className={styles.selectedValueCss}>{selectedValue}</span>
          ) : (
            <span className="sr-only">{t('not_selected')}</span>
          )}
        </span>
        {action}
      </>
    )

    const content = (
      <div
        className={cx(styles.contentCss, contentClassName)}
        {...(as === 'div' ? { role: 'group', 'aria-labelledby': headerId } : {})}
      >
        {children}
      </div>
    )

    if (as === 'div') {
      return (
        <div
          ref={ref as React.Ref<HTMLDivElement>}
          data-testid="product-property"
          className={cx(styles.containerCss, className)}
        >
          <div data-testid={headerTestId} className={cx(styles.headerCss, headerClassName)}>
            {header}
          </div>
          {content}
        </div>
      )
    }

    return (
      <fieldset
        ref={ref as React.Ref<HTMLFieldSetElement>}
        data-testid="product-property"
        className={cx(styles.containerCss, className)}
      >
        <legend data-testid={headerTestId} className={cx(styles.headerCss, headerClassName)}>
          {header}
        </legend>
        {content}
      </fieldset>
    )
  },
)

ProductPropertyDS.displayName = 'ProductPropertyDS'

export default ProductPropertyDS
